import React from "react";
import { createUseStyles } from "react-jss";
import PropTypes from "prop-types";
import _ from "lodash";
import { DateTime } from "luxon";
import jssVariables from "../../jss_setup";
import { useSelector } from "react-redux";
import { humanizeDurationFromMins } from "../../umbilical_tools";

const tripInfoStyles = createUseStyles({
  TripInfo: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content 1fr max-content",
    justifyContent: "space-between",
    alignContent: "baseline",
    alignItems: "center",
    gridGap: `${jssVariables.half_gap} ${jssVariables.normal_gap}`,
    ..._.get(props, "customStyles.TripInfo", {}),
  }),
  originSection: (props) => ({
    display: "grid",
    placeItems: "end",
    gridGap: jssVariables.half_gap,
    ..._.get(props, "customStyles.originSection", {}),
  }),
  port: (props) => ({
    fontSize: "large",
    fontWeight: "500",
    ..._.get(props, "customStyles.port", {}),
  }),
  tripTime: (props) => ({
    fontWeight: "500",
    fontSize: "x-large",
    ..._.get(props, "customStyles.tripTime", {}),
  }),
  tripDate: (props) => ({
    color: "grey",
    ..._.get(props, "customStyles.tripDate", {}),
  }),
  durationSection: (props) => ({
    display: "grid",
    placeItems: "center",
    ..._.get(props, "customStyles.durationSection", {}),
  }),
  duration: (props) => ({
    fontSize: "medium",
    color: "grey",
    ..._.get(props, "customStyles.duration", {}),
  }),
  ferryName: (props) => ({
    fontSize: "small",
    color: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.ferryName", {}),
  }),
  arrowContainer: (props) => ({
    display: "grid",
    paddingTop: `calc(${jssVariables.normal_gap} / 3)`,
    ..._.get(props, "customStyles.arrowContainer", {}),
  }),
  arrow: (props) => ({
    fill: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.arrow", {}),
  }),
  destinationSection: (props) => ({
    display: "grid",
    placeItems: "start",
    gridGap: jssVariables.half_gap,
    ..._.get(props, "customStyles.destinationSection", {}),
  }),
  operatorIcon: (props) => ({
    gridColumn: "span 3",
    justifySelf: "center",
    paddingBottom: jssVariables.normal_gap,
    ..._.get(props, "customStyles.operatorIcon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    TripInfo: (props) => ({
      gridTemplateColumns: props.operatorIcon
        ? "7rem 1fr 7rem 1fr"
        : "max-content 1fr max-content",
      gridColumn: !props.isRoundtrip && "span 3",
      gridGap: `calc(${jssVariables.normal_gap} * 4)`,
      ..._.get(props, "extraStyles.TripInfo.smallscreen", {}),
      ..._.get(props, "customStyles.TripInfo.smallscreen", {}),
    }),
    originSection: (props) => ({
      ..._.get(props, "customStyles.originSection.smallscreen", {}),
    }),
    port: (props) => ({
      fontSize: "large",
      ..._.get(props, "customStyles.port.smallscreen", {}),
    }),
    tripTime: (props) => ({
      fontSize: "x-large",
      ..._.get(props, "customStyles.tripTime.smallscreen", {}),
    }),
    tripDate: (props) => ({
      fontSize: "large",
      ..._.get(props, "customStyles.tripDate.smallscreen", {}),
    }),
    durationSection: (props) => ({
      ..._.get(props, "customStyles.durationSection.smallscreen", {}),
    }),
    duration: (props) => ({
      fontSize: "large",
      ..._.get(props, "customStyles.duration.smallscreen", {}),
    }),
    ferryName: (props) => ({
      fontSize: "large",
      ..._.get(props, "customStyles.ferryName.smallscreen", {}),
    }),
    arrowContainer: (props) => ({
      transform: "scale(1)",
      ..._.get(props, "customStyles.arrowContainer.smallscreen", {}),
    }),
    arrow: (props) => ({
      ..._.get(props, "customStyles.arrow.smallscreen", {}),
    }),
    destinationSection: (props) => ({
      ..._.get(props, "customStyles.destinationSection.smallscreen", {}),
    }),
    operatorIcon: (props) => ({
      gridColumn: "unset",
      ..._.get(props, "customStyles.operatorIcon", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {},
});
export const TripInfo = ({ leg, extraStyles, operatorIcon }) => {
  const { isRoundtrip } = useSelector((state) => state.FerrySearchForm);
  const classes = tripInfoStyles({ isRoundtrip, extraStyles, operatorIcon });

  return (
    <div className={classes.TripInfo}>
      <div className={classes.originSection}>
        <span className={classes.port}>{leg.departure_port_name}</span>
        <span className={classes.tripTime}>
          {DateTime.fromISO(leg.departure_datetime)
            .setZone("UTC", {
              keepLocalTime: false,
            })
            .toLocaleString(DateTime.TIME_SIMPLE)}
        </span>
        <span className={classes.tripDate}>
          {DateTime.fromISO(leg.departure_datetime)
            .setZone("UTC", {
              keepLocalTime: false,
            })
            .toLocaleString(DateTime.DATE_MED)}
        </span>
      </div>
      <div className={classes.durationSection}>
        <div className={classes.duration}>
          {humanizeDurationFromMins(leg.duration_minutes)}
        </div>
        <div className={classes.arrowContainer}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="95"
            height="20"
            viewBox="0 0 95 20"
            fill="none">
            <path
              d="M0.666667 8C0.666666 10.9455 3.05448 13.3333 6 13.3333C8.94552 13.3333 11.3333 10.9455 11.3333 8C11.3333 5.05448 8.94552 2.66667 6 2.66667C3.05448 2.66667 0.666667 5.05448 0.666667 8ZM94.7071 8.70711C95.0976 8.31659 95.0976 7.68343 94.7071 7.2929L88.3431 0.928939C87.9526 0.538415 87.3195 0.538415 86.9289 0.928939C86.5384 1.31946 86.5384 1.95263 86.9289 2.34315L92.5858 8.00001L86.9289 13.6569C86.5384 14.0474 86.5384 14.6806 86.9289 15.0711C87.3195 15.4616 87.9526 15.4616 88.3431 15.0711L94.7071 8.70711ZM6 9L94 9.00001L94 7.00001L6 7L6 9Z"
              className={classes.arrow}
            />
          </svg>
        </div>
        <div className={classes.ferryName}>
          {_.get(leg, "vessel_data.name", "")}
        </div>
      </div>
      <div className={classes.destinationSection}>
        <span className={classes.port}>{leg.arrival_port_name}</span>
        <span className={classes.tripTime}>
          {DateTime.fromISO(leg.arrival_datetime)
            .setZone("UTC", {
              keepLocalTime: false,
            })
            .toLocaleString(DateTime.TIME_SIMPLE)}
        </span>
        <span className={classes.tripDate}>
          {DateTime.fromISO(leg.arrival_datetime)
            .setZone("UTC", {
              keepLocalTime: false,
            })
            .toLocaleString(DateTime.DATE_MED)}
        </span>
      </div>
      {operatorIcon && (
        <img
          src={operatorIcon}
          alt="provider icon"
          className={classes.operatorIcon}
        />
      )}
    </div>
  );
};
TripInfo.defaultProps = {
  leg: {},
  customStyles: {},
  extraStyles: {},
  operatorIcon: "",
};
TripInfo.propTypes = {
  leg: PropTypes.object.isRequired,
  customStyles: PropTypes.object,
  extraStyles: PropTypes.object,
  operatorIcon: PropTypes.string,
};
export default TripInfo;
